<template>
  <div class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:text-gray-800">
    <div class="tw:flex tw:justify-between tw:w-full tw:flex-col">
      <div class="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:text-gray-800">
        <div class="tw:flex tw:justify-between tw:w-full tw:flex-col">
          <div>
            <table class="tw:min-w-full tw:border-none">
              <thead class="tw:hidden tw:md:table-header-group">
                <tr class="tw:border-b tw:border-t">
                  <th
                    v-for="column in columns"
                    :key="column.prop"
                    class="tw:px-5 tw:py-5 tw:border-none tw:bg-white tw:text-left tw:text-sm tw:leading-4 tw:font-bold tw:text-gray-700"
                    :class="`tw:w-[${column.width}]`"
                  >
                    <span>{{ $t(column.label) }}</span>
                  </th>
                </tr>
              </thead>
              <tbody class="tw:bg-white">
                <tr
                  v-for="(user, index) in usersList"
                  :key="index"
                  class="tw:text-sm tw:border-top tw:md:border-none tw:flex tw:md:table-row tw:flex-col"
                >
                  <td class="tw:border-none">
                    <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:bg-blue-200 tw:p-2">
                      <span>{{ $t('form.labels.accounts') }}{{ ': ' }}</span>
                      <span>{{ user.email }}</span>
                    </div>
                    <span class="tw:md:flex tw:hidden">{{ user.email }}</span>
                  </td>
                  <td class="tw:border-none">
                    <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:bg-gray-100 tw:p-2">
                      <span>{{ 'User id' }}{{ ': ' }}</span>
                      <span>{{ user.id }}</span>
                    </div>
                    <span class="tw:md:flex tw:hidden">{{ user.id }}</span>
                  </td>
                  <td class="tw:border-none">
                    <div class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:bg-gray-100 tw:p-2">
                      <span>{{ $t('form.labels.role') }}{{ ': ' }}</span>
                      <span>{{ getRoleLabel(user.role) }}</span>
                    </div>
                    <span class="tw:md:flex tw:hidden">{{ getRoleLabel(user.role) }}</span>
                  </td>
                  <td class="tw:border-none">
                    <div
                      class="tw:flex tw:flex-row tw:md:hidden tw:justify-between tw:w-full tw:bg-gray-100 tw:p-2 tw:lowercase"
                    >
                      <span>{{ $t('Status') }}{{ ': ' }}</span>
                      <span>{{ user.status.toLowerCase() }}</span>
                    </div>
                    <span
                      :class="user.status.toLowerCase() === 'pending' ? 'tw:bg-orange-300' : 'tw:bg-green-500'"
                      class="tw:md:flex tw:hidden tw:w-auto tw:rounded-lg tw:py-1 tw:px-2 tw:w-fit tw:lowercase"
                      >{{ user.status.toLowerCase() }}</span
                    >
                  </td>
                  <td class="tw:border-none tw:px-5 tw:py-3" v-if="organisationId">
                    <template v-if="!user.isInvitation">
                      <UiTooltip
                        v-if="user.removalBlockReason"
                        tooltip-position="top"
                        :label="removalBlockReasonLabel(user.removalBlockReason)"
                      >
                        <DangerButton disabled size="medium">{{ 'Remove' }}</DangerButton>
                      </UiTooltip>
                      <DangerButton v-else size="medium" @click="openKickModal(user)">{{ 'Remove' }}</DangerButton>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <KickUserModal
      v-if="organisationId"
      :isOpen="isKickModalOpen"
      :isKicking="isPending"
      :userEmail="selectedUser?.email"
      @toggleModal="toggleKickModal"
      @confirmKick="confirmKick"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import DangerButton from '@/components/base/buttons/DangerButton.vue'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import KickUserModal from '@/components/adminTool/KickUserModal.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'

import { useKickUserFromOrganisation } from '@/components/adminTool/queries'

const props = defineProps({
  users: Array,
  invitations: Array,
  organisationId: {
    type: String,
    default: null,
  },
})
const { t } = useI18n()
const emit = defineEmits(['fetchData'])
const fetchData = () => emit('fetchData')

const { displayNotification } = useToastNotifications()
const { isPending, kickUserFromOrganisation } = useKickUserFromOrganisation()

const isKickModalOpen = ref(false)
const selectedUser = ref(null)

const getRoleLabel = role => {
  const normalizedRole = String(role ?? '').toLowerCase()

  if (normalizedRole === 'support') return t('tbs_role_hotelier_support')
  if (normalizedRole === 'normal') return t('form.labels.user_role.normal')

  return t('form.labels.user_role.admin')
}

const usersList = computed(() => {
  const realUsers = props.users.map(user => ({
    email: user.email,
    id: user.id ?? '-',
    role: user.role,
    status: 'ACTIVE',
    removalBlockReason: user.removalBlockReason ?? null,
    isInvitation: false,
  }))
  const invitedUsers = props.invitations.map(user => ({
    email: user.invitedEmail,
    id: '-',
    role: user.role,
    status: user.userInvitationStatus ?? 'ACTIVE',
    removalBlockReason: null,
    isInvitation: true,
  }))
  const all = [...realUsers, ...invitedUsers]
  all.sort((a, b) => (a.email > b.email ? 1 : b.email > a.email ? -1 : 0))
  return all
})

const columns = computed(() => {
  const baseColumns = [
    { label: 'User email', prop: 'email', width: '25%' },
    { label: 'User id', prop: 'id', width: '25%' },
    { label: 'User role', prop: 'role', width: '20%' },
    { label: 'Status', prop: 'status', width: '15%' },
  ]

  if (props.organisationId) {
    baseColumns.push({ label: 'Remove', prop: 'remove', width: '15%' })
  }

  return baseColumns
})

const removalBlockReasonMessages = {
  LAST_ADMIN_OF_ORG_WITH_OTHER_USERS:
    "This user can't be removed: they are the last admin of an organisation that still has other users.",
  LAST_ADMIN_AND_ORG_OWNS_PAID_PRODUCT:
    "This user can't be removed: they are the last admin of an organisation that owns active paid products.",
  LAST_ADMIN_AND_NOT_IN_OTHER_ORGS:
    "This user can't be removed: they are the last admin and aren't part of any other organisation.",
}

const removalBlockReasonLabel = reason =>
  removalBlockReasonMessages[reason] ?? "This user can't be removed from the organisation."

const openKickModal = user => {
  selectedUser.value = user
  isKickModalOpen.value = true
}

const toggleKickModal = value => {
  isKickModalOpen.value = value
}

const confirmKick = () => {
  if (!props.organisationId || !selectedUser.value || selectedUser.value.id === '-') return

  kickUserFromOrganisation(
    {
      organisationId: props.organisationId,
      userId: selectedUser.value.id,
    },
    {
      onSuccess: () => {
        isKickModalOpen.value = false
        displayNotification({
          message: 'User removed from the organisation successfully',
          isTranslationKey: false,
          isHtml: false,
          type: 'success',
        })
        fetchData()
      },
      onError: error => {
        isKickModalOpen.value = false
        const backendMessage = error.response?.data?.message
        displayNotification({
          message: backendMessage ?? 'something_went_wrong',
          isTranslationKey: !backendMessage,
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}
</script>
